import {
  TextField,
  OutlinedInput,
  FormControl,
  InputLabel,
  InputAdornment,
  InputBaseComponentProps,
} from "@material-ui/core";
import { compose } from "ramda";
import { memo, NamedExoticComponent, useEffect } from "react";
import { UseFormMethods, Control, Controller } from "react-hook-form";

import { withError } from "../HOC";
import { MaskType, MaskedInput } from "../MaskedInput";
import { Tooltip } from "../Tooltip";
import { CommonFormElProps } from "../types";

export type InputProps = {
  readonly control: Control;
  readonly register: UseFormMethods["register"];
  readonly setValue: UseFormMethods["setValue"];
  readonly placeholder?: string;
  readonly mask?: MaskType;
  readonly suffix?: string;
  readonly isNumber?: boolean;
  readonly prefix?: string;
  readonly tooltip?: string;
} & CommonFormElProps;

export const Input = memo<InputProps>(
  ({
    control,
    defaultValue = "",
    disabled: isDisabled = false,
    hasError,
    id,
    label: labelTxt,
    mask,
    placeholder,
    prefix,
    register,
    required = false,
    setValue,
    suffix,
    isNumber = false,
    tooltip,
  }) => {
    const endAdornment = suffix && (
      <InputAdornment position="end">{suffix}</InputAdornment>
    );
    const startAdornment = prefix && (
      <InputAdornment position="start">{prefix}</InputAdornment>
    );
    const type = (isNumber && "number") || "text";
    const label = (
      <>
        {labelTxt}
        <Tooltip copy={tooltip} />
      </>
    );

    useEffect(() => {
      setValue(id, defaultValue);
    }, [defaultValue, id, setValue]);

    if (!mask) {
      return (
        <TextField
          autoComplete="off"
          defaultValue={defaultValue}
          disabled={isDisabled}
          error={hasError}
          id={id}
          inputRef={register}
          InputProps={{
            name: id,
            endAdornment,
            notched: false,
            placeholder,
            startAdornment,
            type,
          }}
          InputLabelProps={{
            disabled: isDisabled,
            required: !isDisabled && required,
            shrink: true,
          }}
          label={label}
          variant="outlined"
        />
      );
    }

    return (
      <FormControl error={hasError}>
        <InputLabel
          shrink
          disabled={isDisabled}
          error={hasError}
          htmlFor={id}
          required={!isDisabled && required}
        >
          {label}
        </InputLabel>
        <Controller
          name={id}
          control={control}
          defaultValue={defaultValue}
          as={
            <OutlinedInput
              autoComplete="off"
              defaultValue={defaultValue}
              disabled={isDisabled}
              endAdornment={endAdornment}
              error={hasError}
              id={id}
              inputComponent={
                MaskedInput as NamedExoticComponent<InputBaseComponentProps>
              }
              inputProps={{
                mask,
                name: id,
                placeholder,
                type,
              }}
              startAdornment={startAdornment}
            />
          }
        ></Controller>
      </FormControl>
    );
  }
);

export const InputWithError = compose(withError)(Input);
